import React from "react";
import {
  Col,
  Form,
  Row,
  Space,
  Radio,
  Button,
  Table,
  Select,
  Typography,
  Card,
  Divider,
  Input,
  FormInstance,
} from "antd";
import { ConformityCertificateType } from "./types";
import get from "lodash/get";
import styled from "@emotion/styled";
import { DoCs } from "../DoC";
import { CPCItems, DOCItems } from "constants/DoC";
import { getConformityColDefs } from "constants/columnDef";
import { DynamicFormModal } from "../modals";
import { useAuth } from "context/auth-context";
import TestLineTable from "./TestLineTable";

const RadioGroup = Radio.Group;
const StyledSpaceFullWidth = styled(Space)`
  width: 100%;
`;
const Title = styled.h3`
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.015em;
  text-align: left;
  margin-top: 24px;
`;

const Spacer = styled.div``;
const CONFORMITY = {
  CPC: "CPC",
  DOC: "DOC",
  "report-data": "Report Data",
};
export const ConformityCertificate = ({
  statusName = "",
  testLinesName = "",
  conformityCertificateName = "",
  onAddTestLine,
  onRemoveTestLine,
  onEditTestLine,
  disabled,
  onEditGeneralInformation,
  onClickGenerate,
  product,
  compliance,
  form,
}: ConformityCertificateType) => {
  const { user } = useAuth();
  const status = Form.useWatch(statusName, form);

  const [showTestLines, setShowTestLines] = React.useState(false);
  const [conformityType, setConformityType] = React.useState("DOC");
  const [editModalItem, setEditModalItem] = React.useState({
    visible: false,
    fields: [],
    initialValues: {},
    rawData: {},
    name: "",
    title: "",
  });

  const getDocItem = (name: string) => {
    return [...DOCItems, ...CPCItems].find(
      ({ name: docName }) => docName === name
    );
  };

  const defaultValue = React.useMemo(() => {
    const items =
      conformityType === "DOC"
        ? ["company", "product"]
        : ["product", "importedManfacturer", "manufacturingInformation"];
    if (!product || !compliance) return [];

    return items.map((docName) => {
      const item = getDocItem(docName);
      return {
        title: item.title,
        ...item.parsedDefaultValues(product, compliance?.merchant ?? {}),
      };
    });
  }, [conformityType, product, compliance]);

  React.useEffect(() => {
    if (compliance.conformityCertificateType) {
      setConformityType(compliance.conformityCertificateType);
    }
  }, [compliance.conformityCertificateType]);

  React.useEffect(() => {
    setShowTestLines(status === "conformity_certificate_required");
  }, [status]);

  return (
    <>
      <StyledSpaceFullWidth direction="vertical">
        <Title>Conformity Certificate</Title>
        <Row>
          <Col lg={24}>
            <Form.Item
              name={statusName}
              getValueFromEvent={(event) => {
                setShowTestLines(
                  event.target.value === "conformity_certificate_required"
                );
                return event.target.value;
              }}
              shouldUpdate={(_, nextValue) => {
                const status = get(
                  nextValue,
                  statusName,
                  "conformity_certificate_not_required"
                );
                setShowTestLines(status === "conformity_certificate_required");
                return true;
              }}
            >
              <RadioGroup
                disabled={disabled}
                options={[
                  {
                    value: "conformity_certificate_not_required",
                    label: "Conformity Certificate Not Required",
                  },
                  {
                    value: "conformity_certificate_required",
                    label: "Conformity Certificate Required",
                  },
                  {
                    value: "merchant_uploaded",
                    label: "Merchant Uploaded",
                  },
                ]}
              ></RadioGroup>
            </Form.Item>
          </Col>
        </Row>

        {showTestLines && (
          <>
            <Row align="middle">
              <Col lg={4}>
                <Form.Item name="conformityCertificateType">
                  <Select
                    disabled={disabled}
                    defaultValue="report-data"
                    onChange={(value) => {
                      setConformityType(value);
                    }}
                  >
                    <Select.Option value="DOC">DOC</Select.Option>
                    <Select.Option value="CPC">CPC</Select.Option>
                    <Select.Option value="report-data">
                      Report Data
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <StyledSpaceFullWidth direction="vertical">
              <Card
                title={
                  <Space direction="vertical">
                    <Title>{CONFORMITY[conformityType]}</Title>
                    <Typography.Text>
                      {conformityType !== "report-data" &&
                        "Add test lines for generating conformity certificate"}
                    </Typography.Text>
                  </Space>
                }
                extra={
                  conformityType !== "report-data" && (
                    <Button
                      type="primary"
                      ghost
                      onClick={() => {
                        onClickGenerate &&
                          onClickGenerate(conformityType, defaultValue);
                      }}
                    >
                      Generate
                    </Button>
                  )
                }
              >
                <StyledSpaceFullWidth direction="vertical" size={[16, 16]}>
                  {["docTestLines", "cpcTestLines", "reportDataTestLines"].map(
                    (fieldName) => {
                      const hidden = !fieldName.includes(
                        (conformityType === "report-data"
                          ? "report"
                          : conformityType
                        ).toLowerCase()
                      );
                      const value = form.getFieldValue([
                        "testLines",
                        fieldName,
                      ]);
                      return (
                        <Form.Item
                          key={fieldName}
                          name={[testLinesName as string, fieldName]}
                          hidden={hidden}
                        >
                          <TestLineTable
                            onAddTestLine={onAddTestLine}
                            disabled={disabled}
                            conformityType={conformityType}
                            onEditTestLine={onEditTestLine}
                            onRemoveTestLine={onRemoveTestLine}
                          ></TestLineTable>
                        </Form.Item>
                      );
                    }
                  )}
                </StyledSpaceFullWidth>

                <Divider></Divider>

                <StyledSpaceFullWidth
                  hidden={conformityType === "report-data"}
                  direction="vertical"
                  size={[16, 16]}
                >
                  <Space direction="vertical">
                    <Title>General Information</Title>
                    <Typography.Text>
                      Information that generated by merchant account info,
                      please review and amend if needed
                    </Typography.Text>
                    <Spacer></Spacer>
                  </Space>
                  <div>
                    <Form.Item name={["testLines", "docGeneralInformations"]}>
                      <DoCs
                        hidden={conformityType !== "DOC"}
                        items={DOCItems}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        onClickEdit={(name, docItem, data) => {
                          const doc = getDocItem(name);

                          setEditModalItem({
                            ...editModalItem,
                            title: doc?.title ?? "",
                            fields: docItem.fields,
                            visible: true,
                            initialValues: Object.assign(
                              docItem.initialValues,
                              data
                            ),
                            rawData: data,
                            name,
                          });
                        }}
                      ></DoCs>
                    </Form.Item>
                    <Form.Item name={["testLines", "cpcGeneralInformations"]}>
                      <DoCs
                        hidden={conformityType === "DOC"}
                        items={CPCItems}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        onClickEdit={(name, docItem, data) => {
                          const doc = getDocItem(name);

                          setEditModalItem({
                            ...editModalItem,
                            title: doc?.title ?? "",
                            fields: docItem.fields,
                            visible: true,
                            initialValues: Object.assign(
                              docItem.initialValues,
                              data
                            ),
                            rawData: data,
                            name,
                          });
                        }}
                      ></DoCs>
                    </Form.Item>
                  </div>
                  <Col span={12}>
                    <Form.Item
                      label="Additional Information (optional)"
                      name={["testLines", "docAdditionalInformation"]}
                      hidden={conformityType !== "DOC"}
                    >
                      <Input.TextArea
                        placeholder="Enter additional information"
                        autoSize={{ minRows: 5, maxRows: 5 }}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Additional Information (optional)"
                      name={["testLines", "cpcAdditionalInformation"]}
                      hidden={conformityType !== "CPC"}
                    >
                      <Input.TextArea
                        placeholder="Enter additional information"
                        autoSize={{ minRows: 5, maxRows: 5 }}
                      />
                    </Form.Item>
                  </Col>
                </StyledSpaceFullWidth>
              </Card>
            </StyledSpaceFullWidth>
          </>
        )}
      </StyledSpaceFullWidth>
      <DynamicFormModal
        onOk={(form: FormInstance) => {
          onEditGeneralInformation &&
            onEditGeneralInformation(conformityType, {
              ...form.getFieldsValue(),
              title: editModalItem.title,
            });
          setEditModalItem({
            visible: false,
            fields: [],
            initialValues: {},
            rawData: {},
            name: "",
            title: "",
          });
        }}
        onCancel={() => {
          setEditModalItem({
            visible: false,
            fields: [],
            initialValues: {},
            rawData: {},
            name: "",
            title: "",
          });
        }}
        visible={editModalItem.visible}
        formName={editModalItem.name}
        title={editModalItem.title}
        rawData={editModalItem.rawData}
        fields={editModalItem.fields}
        initialValues={editModalItem.initialValues}
      ></DynamicFormModal>
    </>
  );
};
